import type {
  AdminAuditEntry,
  AdminCertificate,
  AdminCohort,
  AdminContentItem,
  AdminEvidence,
  AdminParticipant,
  AdminSession,
  AssessmentDimensionId,
  AssessmentInstrument,
  AssessmentSubmission,
  Cohort,
  Identity,
  ParticipantProgress
} from "./types.ts";

export const demoCohort: Cohort = {
  id: "cohort-inspl-2026-demo",
  name: "INSPL 2026 · Piloto Familias que Acompañan",
  timezone: "Europe/Madrid",
  accessEndsAt: "2026-10-31T22:59:59.000Z",
  closingSessionAt: "2026-10-08T16:00:00.000Z",
  modules: [
    { id: "m1", order: 1, shortTitle: "Módulo 1", title: "Adolescencia, bienestar emocional y vínculo en la era digital", opensAt: "2026-09-01T06:00:00.000Z", requiredPieces: 4, estimatedMinutes: 100, summary: "Entender qué está cambiando antes de entrar en normas, tiempos de uso o controles." },
    { id: "m2", order: 2, shortTitle: "Módulo 2", title: "Comunicación emocional y conflicto familiar", opensAt: "2026-09-14T06:00:00.000Z", requiredPieces: 4, estimatedMinutes: 150, summary: "Aprender a detectar la escalada, escuchar, validar, hacer una pausa y reparar sin renunciar al papel adulto." },
    { id: "m3", order: 3, shortTitle: "Módulo 3", title: "Límites que cuidan, acuerdos digitales y autonomía", opensAt: "2026-09-21T06:00:00.000Z", requiredPieces: 4, estimatedMinutes: 115, summary: "Proteger lo importante con límites claros, acuerdos revisables y autonomía progresiva." },
    { id: "m4", order: 4, shortTitle: "Módulo 4", title: "Presión social, autoestima, redes y riesgos", opensAt: "2026-09-28T06:00:00.000Z", requiredPieces: 4, estimatedMinutes: 115, summary: "Reconocer presión y riesgos, abrir conversaciones y saber cuándo pedir ayuda." }
  ]
};

// New production registrations use a separate cohort so earlier validation
// records remain available without affecting the live pilot's metrics.
export const productionCohort: Cohort = {
  ...demoCohort,
  id: "cohort-familias-2026-piloto",
  name: "Familias que Acompañan · Piloto 2026",
  unlimitedAccess: true,
  closingSessionScheduled: false,
  heldModuleIds: [],
  // Pilot launch on 16 October: one module opens each Friday at 08:00 Madrid.
  modules: demoCohort.modules.map((module, index) => ({
    ...module,
    opensAt: ["2026-10-16T06:00:00.000Z", "2026-10-23T06:00:00.000Z", "2026-10-30T07:00:00.000Z", "2026-11-06T07:00:00.000Z"][index]
  }))
};

export const demoIdentities: Identity[] = [
  { participantId: "p-revision-34", cohortId: demoCohort.id, displayName: "Nerea · revisión módulos 3 y 4", role: "participant", authorizationStatus: "authorized", scenarioNow: "2026-10-02T12:00:00.000Z" },
  { participantId: "p-inicio", cohortId: demoCohort.id, displayName: "Laura · inicio", role: "participant", authorizationStatus: "authorized", scenarioNow: "2026-09-08T12:00:00.000Z" },
  { participantId: "p-calendario", cohortId: demoCohort.id, displayName: "Marta · apertura semanal", role: "participant", authorizationStatus: "authorized", scenarioNow: "2026-09-16T12:00:00.000Z" },
  { participantId: "p-cierre", cohortId: demoCohort.id, displayName: "Álex · cierre disponible", role: "participant", authorizationStatus: "authorized", scenarioNow: "2026-10-02T12:00:00.000Z" },
  { participantId: "p-sin-acceso", cohortId: demoCohort.id, displayName: "Usuario · sin autorización", role: "participant", authorizationStatus: "unauthorized", scenarioNow: "2026-09-16T12:00:00.000Z" },
  { participantId: "admin-demo", cohortId: demoCohort.id, displayName: "Administración · ficticia", role: "admin", authorizationStatus: "authorized", scenarioNow: "2026-10-02T12:00:00.000Z" }
];

export const demoProgress: Record<string, ParticipantProgress> = {
  "p-revision-34": { participantId: "p-revision-34", cohortId: demoCohort.id, onboardingCompleted: true, pretestCompleted: true, completedPieces: { m1: 4, m2: 4 }, finalAssessmentCompleted: false, satisfactionCompleted: false, activityStatus: {}, downloadedResources: [], webinarAttendance: "unverified", closingAttendance: "unverified" },
  "p-inicio": { participantId: "p-inicio", cohortId: demoCohort.id, onboardingCompleted: false, pretestCompleted: false, completedPieces: {}, finalAssessmentCompleted: false, satisfactionCompleted: false, activityStatus: {}, downloadedResources: [], webinarAttendance: "unverified", closingAttendance: "unverified" },
  "p-calendario": { participantId: "p-calendario", cohortId: demoCohort.id, onboardingCompleted: true, pretestCompleted: true, completedPieces: { m1: 0, m2: 1 }, finalAssessmentCompleted: false, satisfactionCompleted: false, activityStatus: { "activity-m2": "partial" }, downloadedResources: [], webinarAttendance: "absent", closingAttendance: "unverified" },
  "p-cierre": { participantId: "p-cierre", cohortId: demoCohort.id, onboardingCompleted: true, pretestCompleted: true, completedPieces: { m1: 4, m2: 4, m3: 4, m4: 4 }, finalAssessmentCompleted: false, satisfactionCompleted: false, activityStatus: { "activity-m1": "completed", "activity-m2": "partial" }, downloadedResources: ["resource-m1"], webinarAttendance: "absent", closingAttendance: "unverified" },
  "admin-demo": { participantId: "admin-demo", cohortId: demoCohort.id, onboardingCompleted: true, pretestCompleted: true, completedPieces: { m1: 4, m2: 2 }, finalAssessmentCompleted: false, satisfactionCompleted: false, activityStatus: {}, downloadedResources: [], webinarAttendance: "present", closingAttendance: "unverified" }
};

export const resourcePdfUrls = {
  m1: "https://institutobienestardigital.com/wp-content/uploads/2026/10/M1_Mapa-de-vinculo-familiar.pdf",
  m2: "https://institutobienestardigital.com/wp-content/uploads/2026/10/M2_Rebobina-una-discusion.pdf",
  m3: "https://institutobienestardigital.com/wp-content/uploads/2026/10/Acuerdo-digital-familiar_Bienestar-digital.pdf"
};

export const demoResources = [
  { id: "resource-m1", moduleId: "m1", title: "Mapa de vínculo familiar · Mirar antes de corregir", description: "Útil para… reconocer qué os acerca, qué genera tensión y qué podrías preguntar antes de intentar corregir.", kind: "worksheet", available: true },
  { id: "resource-m2", moduleId: "m2", title: "Rebobina una discusión", description: "Útil para... revisar un desacuerdo y ensayar otra forma de responder.", kind: "pdf", available: true },
  { id: "resource-m3", moduleId: "m3", title: "Nuestro acuerdo digital familiar", description: "Útil para… concretar una norma digital, recoger los compromisos de todos y decidir cuándo revisarla.", kind: "worksheet", available: true },
  { id: "resource-m4", moduleId: "m4", title: "Cuando algo me preocupa · Guía para observar, conversar y pedir ayuda", description: "Útil para… valorar si conviene observar, conversar o pedir ayuda ante una situación digital que te preocupa.", kind: "worksheet", available: false },
  { id: "final-guide", title: "Guía final para continuar en casa", description: "Se desbloquea al completar postest y satisfacción.", kind: "guide", available: false }
] as const;

export const demoAdminCohorts: AdminCohort[] = [
  {
    id: demoCohort.id,
    name: "INSPL 2026 · Cohorte ficticia",
    status: "active",
    startsAt: "2026-09-03T16:00:00.000Z",
    endsAt: "2026-10-31T22:59:59.000Z",
    targetParticipants: 100,
    courseVersion: "course-2026-v0.8.7",
    legalVersion: "legal-demo-v1"
  },
  {
    id: "cohort-2027-demo",
    name: "Cohorte 2027 · Borrador",
    status: "draft",
    startsAt: "2027-02-04T17:00:00.000Z",
    endsAt: "2027-03-31T21:59:59.000Z",
    targetParticipants: 100,
    courseVersion: "course-2027-draft",
    legalVersion: "legal-pending"
  }
];

const localities = ["Pamplona", "Tudela", "Corella", "Estella-Lizarra", "Tafalla", "Burlada"];
const channels = ["APYMA", "Servicios Sociales", "Centro educativo", "Redes", "Derivación comunitaria"];
const names = ["Laura Jiménez", "Marta Ruiz", "Álex García", "Nerea López", "Irene Sanz", "David Pérez", "Ainhoa Martín", "Javier Ortiz"];

export const demoAdminParticipants: AdminParticipant[] = Array.from({ length: 96 }, (_, index) => {
  const number = index + 1;
  const completed = index < 41;
  const started = index < 82;
  const completedModules = completed ? 4 : index < 50 ? 3 : index < 60 ? 2 : index < 72 ? 1 : 0;
  const status = completed ? "completed" : started ? "started" : "registered";
  const participantId = `participant-${String(number).padStart(3, "0")}`;
  const sex = index % 5 === 4 ? "No indicado" : index % 3 === 0 ? "Hombre" : "Mujer";
  return {
    participantId,
    pseudonym: `P-${String(number).padStart(4, "0")}`,
    cohortId: demoCohort.id,
    name: names[index] ?? `Participante ${String(number).padStart(3, "0")}`,
    email: `participante${String(number).padStart(3, "0")}@example.invalid`,
    sex,
    locality: localities[index % localities.length],
    channel: channels[index % channels.length],
    relationship: index % 7 === 0 ? "Padre" : index % 11 === 0 ? "Tutor/a" : "Madre",
    registeredAt: new Date(Date.UTC(2026, 7, 18 + (index % 16), 8, index % 60)).toISOString(),
    lastAccessAt: started ? new Date(Date.UTC(2026, 8, 8 + (index % 24), 7 + (index % 10), index % 60)).toISOString() : undefined,
    status,
    progressPercent: completed ? 100 : started ? Math.max(13, completedModules * 20 + (index % 3) * 5) : 0,
    completedModules,
    pretestCompleted: started,
    posttestCompleted: completed,
    satisfactionScore: completed ? (index % 6 === 0 ? 3 : index % 3 === 0 ? 5 : 4) : undefined,
    activitiesCompleted: started && index % 3 !== 0 ? Math.min(4, completedModules + 1) : 0,
    webinarAttendance: index % 3 === 0 ? "absent" : index % 7 === 0 ? "unverified" : "present",
    closingAttendance: completed ? (index % 4 === 0 ? "absent" : "present") : "unverified",
    certificateStatus: completed ? (index < 38 ? "issued" : "eligible") : "not_eligible",
    certificateId: completed && index < 38 ? `FQA-2026-${String(number).padStart(4, "0")}` : undefined
  } satisfies AdminParticipant;
});

const evaluationItems: AssessmentInstrument["items"] = [
  { id: "item-communication", dimension: "communication", label: "Podemos hablar de lo digital incluso cuando hay tensión." },
  { id: "item-agreements", dimension: "agreements", label: "Podemos alcanzar acuerdos digitales dialogados." },
  { id: "item-conflict", dimension: "conflict", label: "Tengo recursos para afrontar el conflicto familiar." },
  { id: "item-emotional-support", dimension: "emotional_support", label: "Sé acompañar emocionalmente a mi hijo o hija." },
  { id: "item-parental-competence", dimension: "parental_competence", label: "Me siento capaz de acompañar el bienestar digital familiar." }
];

export const demoAssessmentInstruments: AssessmentInstrument[] = [
  {
    id: "instrument-pre-inspl-2026-demo",
    pairingKey: "fqa-inspl-2026-core-demo-v1",
    kind: "pre",
    version: 1,
    status: "active",
    title: "Punto de partida · instrumento provisional versionado",
    scaleMin: 1,
    scaleMax: 5,
    items: evaluationItems
  },
  {
    id: "instrument-post-inspl-2026-demo",
    pairingKey: "fqa-inspl-2026-core-demo-v1",
    kind: "post",
    version: 1,
    status: "active",
    title: "Evaluación final · instrumento provisional versionado",
    scaleMin: 1,
    scaleMax: 5,
    items: evaluationItems
  },
  {
    id: "instrument-satisfaction-inspl-2026-demo",
    pairingKey: "fqa-inspl-2026-satisfaction-demo-v1",
    kind: "satisfaction",
    version: 1,
    status: "active",
    title: "Satisfacción · instrumento provisional versionado",
    scaleMin: 1,
    scaleMax: 5,
    items: [
      { id: "item-utility", dimension: "utility", label: "El itinerario me ha resultado útil." },
      { id: "item-overall-satisfaction", dimension: "overall_satisfaction", label: "Valoración global del itinerario." }
    ]
  }
];

const evaluationDimensions: AssessmentDimensionId[] = ["communication", "agreements", "conflict", "emotional_support", "parental_competence"];
const improvedCounts: Record<AssessmentDimensionId, number> = {
  communication: 27,
  agreements: 26,
  conflict: 25,
  emotional_support: 28,
  parental_competence: 29
};
const declinedCounts: Record<AssessmentDimensionId, number> = {
  communication: 4,
  agreements: 4,
  conflict: 4,
  emotional_support: 4,
  parental_competence: 3
};

function baseEvaluationAnswers(index: number): Record<string, number> {
  return Object.fromEntries(evaluationDimensions.map((dimension, dimensionIndex) => [dimension, 2 + ((index + dimensionIndex) % 2)]));
}

function postEvaluationAnswers(index: number): Record<string, number> {
  const pre = baseEvaluationAnswers(index);
  return Object.fromEntries(evaluationDimensions.map((dimension) => {
    const change = index < improvedCounts[dimension] ? 1 : index >= 41 - declinedCounts[dimension] ? -1 : 0;
    return [dimension, Math.max(1, Math.min(5, pre[dimension] + change))];
  }));
}

export const demoAssessmentSubmissions: AssessmentSubmission[] = demoAdminParticipants.flatMap((participant, index) => {
  if (!participant.pretestCompleted) return [];
  const submissions: AssessmentSubmission[] = [{
    id: `submission-pre-${participant.participantId}`,
    participantId: participant.participantId,
    cohortId: participant.cohortId,
    instrumentId: "instrument-pre-inspl-2026-demo",
    pairingKey: "fqa-inspl-2026-core-demo-v1",
    instrumentVersion: 1,
    kind: "pre",
    submittedAt: new Date(Date.UTC(2026, 8, 3 + (index % 8), 8, index % 60)).toISOString(),
    answers: baseEvaluationAnswers(index)
  }];
  if (!participant.posttestCompleted) return submissions;
  submissions.push({
    id: `submission-post-${participant.participantId}`,
    participantId: participant.participantId,
    cohortId: participant.cohortId,
    instrumentId: "instrument-post-inspl-2026-demo",
    pairingKey: "fqa-inspl-2026-core-demo-v1",
    instrumentVersion: 1,
    kind: "post",
    submittedAt: new Date(Date.UTC(2026, 9, 2 + (index % 7), 17, index % 60)).toISOString(),
    answers: postEvaluationAnswers(index)
  });
  submissions.push({
    id: `submission-satisfaction-${participant.participantId}`,
    participantId: participant.participantId,
    cohortId: participant.cohortId,
    instrumentId: "instrument-satisfaction-inspl-2026-demo",
    pairingKey: "fqa-inspl-2026-satisfaction-demo-v1",
    instrumentVersion: 1,
    kind: "satisfaction",
    submittedAt: new Date(Date.UTC(2026, 9, 2 + (index % 7), 17, (index % 60) + 1)).toISOString(),
    answers: {
      utility: index % 7 === 0 ? 3 : index % 3 === 0 ? 5 : 4,
      overall_satisfaction: participant.satisfactionScore ?? 4
    }
  });
  return submissions;
});

export const demoAdminContent: AdminContentItem[] = [
  { id: "content-welcome", cohortId: demoCohort.id, type: "welcome", title: "Bienvenida al recorrido", order: 1, status: "published", updatedAt: "2026-08-24T09:15:00.000Z", version: "v1" },
  ...demoCohort.modules.map((module) => ({ id: `content-${module.id}`, cohortId: demoCohort.id, type: "module" as const, title: module.title, order: module.order + 1, status: "published" as const, updatedAt: "2026-08-24T09:15:00.000Z", version: "v1-provisional" })),
  { id: "content-resource", cohortId: demoCohort.id, type: "resource", title: "Recursos descargables", order: 6, status: "published", updatedAt: "2026-08-24T09:15:00.000Z", version: "v1-provisional" },
  { id: "content-closing", cohortId: demoCohort.id, type: "closing", title: "Cierre, postest y satisfacción", order: 7, status: "published", updatedAt: "2026-08-24T09:15:00.000Z", version: "v1-provisional" },
  { id: "content-guide", cohortId: demoCohort.id, type: "guide", title: "Guía final", order: 8, status: "draft", updatedAt: "2026-08-24T09:15:00.000Z", version: "draft-1" }
];

export const demoAdminSessions: AdminSession[] = [
  { id: "webinar-demo", cohortId: demoCohort.id, type: "webinar", title: "Webinar inaugural", startsAt: "2026-09-03T16:00:00.000Z", durationMinutes: 90, meetStatus: "completed" },
  { id: "closing-demo", cohortId: demoCohort.id, type: "closing", title: "Encuentro comunitario de cierre", startsAt: demoCohort.closingSessionAt, durationMinutes: 90, meetStatus: "scheduled" }
];

export const demoAdminCertificates: AdminCertificate[] = demoAdminParticipants
  .filter((participant) => participant.certificateStatus !== "not_eligible")
  .map((participant, index) => ({
    participantId: participant.participantId,
    certificateId: participant.certificateId,
    cohortId: participant.cohortId,
    status: participant.certificateStatus,
    issuedAt: participant.certificateStatus === "issued" ? new Date(Date.UTC(2026, 9, 2 + (index % 6), 9, 0)).toISOString() : undefined,
    version: 1
  }));

export const demoAdminEvidence: AdminEvidence[] = [
  { id: "evidence-content-v1", cohortId: demoCohort.id, type: "content_version", title: "Versión del itinerario v1-provisional", status: "ready", createdAt: "2026-08-24T09:15:00.000Z", containsIdentifiedData: false },
  { id: "evidence-webinar", cohortId: demoCohort.id, type: "attendance", title: "Registro de asistencia · webinar", status: "ready", createdAt: "2026-09-04T08:00:00.000Z", containsIdentifiedData: true },
  { id: "evidence-progress", cohortId: demoCohort.id, type: "snapshot", title: "Snapshot de progreso · semana 2", status: "ready", createdAt: "2026-09-18T08:00:00.000Z", containsIdentifiedData: false },
  { id: "evidence-certificates", cohortId: demoCohort.id, type: "certificate", title: "Registro de certificados", status: "pending", createdAt: "2026-10-02T10:00:00.000Z", containsIdentifiedData: true },
  { id: "evidence-close", cohortId: demoCohort.id, type: "snapshot", title: "Snapshot final de cohorte", status: "pending", createdAt: "2026-10-02T10:00:00.000Z", containsIdentifiedData: false }
];

export const demoAdminAudit: AdminAuditEntry[] = [
  { id: "audit-001", cohortId: demoCohort.id, adminId: "admin-demo", action: "admin_access", targetType: "admin", targetId: "admin-demo", createdAt: "2026-10-02T11:50:00.000Z" },
  { id: "audit-002", cohortId: demoCohort.id, adminId: "admin-demo", action: "content_change", targetType: "content", targetId: "content-m2", field: "status", previousValue: "draft", newValue: "published", reason: "Publicación semanal prevista", createdAt: "2026-09-14T06:00:00.000Z" },
  { id: "audit-003", cohortId: demoCohort.id, adminId: "admin-demo", action: "manual_correction", targetType: "participant", targetId: "participant-004", field: "webinarAttendance", previousValue: "unverified", newValue: "present", reason: "Comprobado en el registro de entrada", createdAt: "2026-09-04T09:30:00.000Z" },
  { id: "audit-004", cohortId: demoCohort.id, adminId: "admin-demo", action: "evidence_snapshot", targetType: "evidence", targetId: "evidence-progress", createdAt: "2026-09-18T08:00:00.000Z" }
];
